import { gql, useMutation } from "@apollo/client";
import { useEffect, useState, useRef } from "react";
import { Controller, FieldPath, useFormContext } from "react-hook-form";
import { View, FlatList } from "react-native";
import { Text } from "react-native-paper";

import StateDropdown from "./StateDropdown";
import useIntercom from "../../../hooks/useIntercom";
import {
  Button,
  CheckboxItem,
  KeyboardAvoidingView,
  useErrorScroll,
  ActionsContainer,
  ScreenContainer,
  ScrollableContainer,
} from "../../../components";
import { CURRENT_MEMBER_FRAGMENT } from "../../fragments";
import { defaultValues, FormData } from "../formData";
import TextInput from "./TextInput";
import SSNInput from "./SSNInput";
import type { ScreenProps } from "..";
import AddressAutocompleteInput from "../../../components/AddressAutoComplete";

export const MUTATION = gql`
  mutation createEnrollment($input: DeferredCompCreateEnrollmentInput!) {
    createDeferredCompEnrollment(input: $input) {
      message
      success
      member {
        ...CurrentMemberFragment
      }
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

interface Mutation {
  createDeferredCompEnrollment: { success: boolean; message: string };
}

export type ParamsList = { Form: undefined; Success: undefined };

export default function AccountDetailsScreen({ navigation: { navigate } }: ScreenProps<"AccountDetails">) {
  const [enroll, { loading }] = useMutation<Mutation>(MUTATION);

  async function onSubmit({ acceptedTerms, attested, ...data }: FormData) {
    try {
      const { success, message } = (await enroll({ variables: { input: data } }))?.data?.createDeferredCompEnrollment || {};

      if (!success) {
        throw new Error(message);
      }
    } catch (e) {
      setErrorMessage((e as Error).message || "Something went wrong");
    }
  }

  const {
    control,
    handleSubmit,
    getValues,
    setFocus,
    formState: { isValid, isSubmitting },
  } = useFormContext<FormData>();

  useEffect(() => {
    if (!getValues().acceptedTerms) {
      navigate("Overview");
    }
  }, []);

  useIntercom({
    onFocus: { event: "viewed-account-details-form" },
  });

  const flatListRef = useRef<FlatList>(null);

  const { Divider, onSubmitError, scrollToField } = useErrorScroll({
    defaultValues,
    flatListRef,
  });

  const [errorMessage, setErrorMessage] = useState("");

  const focus = (field: FieldPath<FormData>) => () => {
    setFocus(field);
  };

  return (
    <ScreenContainer navigationTitle="Account Details" goBack={() => navigate("Terms")}>
      <KeyboardAvoidingView>
        <ScrollableContainer ref={flatListRef} keyboardShouldPersistTaps="handled">
          <View className="mx-7 pt-8 mb-9">
            <Text className="text-ink text-small-normal font-400">
              The plan administrator requests the following information to create a deferred compensation account.
            </Text>
          </View>

          <Divider fields={["firstName", "lastName"]} />

          <View className="px-7 mb-6">
            <Text className="mt-7 text-ink-darkest text-large-normal font-700">Legal Name</Text>

            <Text className="mt-2 mb-5 text-small-normal font-400 text-ink-dark">
              As shown on your driver's license or government-issued ID.
            </Text>

            <TextInput
              autoComplete="name-given"
              className="my-1.5"
              control={control}
              label="First Name"
              name="firstName"
              onSubmitEditing={focus("middleName")}
              rules={{ required: "required" }}
              textContentType="givenName"
            />

            <TextInput
              autoComplete="name-middle"
              className="my-1.5 w-2/3"
              control={control}
              label="Middle Name or Initial"
              name="middleName"
              onSubmitEditing={focus("lastName")}
              textContentType="middleName"
            />

            <TextInput
              autoComplete="name-family"
              className="my-1.5"
              control={control}
              label="Last Name"
              name="lastName"
              onSubmitEditing={focus("address")}
              rules={{ required: "required" }}
              textContentType="familyName"
            />
          </View>

          <Divider fields={["address", "city", "state", "postalCode"]} />

          <View className="px-7 mb-8">
            <Text className="mt-7 text-ink-darkest text-large-normal font-700">Address</Text>

            <Text className="mt-2 mb-7 text-small-normal font-400 text-ink-dark">
              This is where the plan administrator will mail information regarding your account.
            </Text>

            <AddressAutocompleteInput
              autoComplete="postal-address"
              control={control}
              label="Address"
              name="address"
              onSubmitEditing={focus("address2")}
              rules={{ required: "required" }}
              textContentType="streetAddressLine1"
              onFocus={() => {
                scrollToField("address");
              }}
            />

            <Text className="text-ink-lighter text-small-normal my-2">(number, street)</Text>

            <TextInput
              autoComplete="postal-address-extended"
              className="my-1.5"
              control={control}
              label="Apt. or Suite"
              name="address2"
              onSubmitEditing={focus("city")}
              textContentType="streetAddressLine2"
            />

            <TextInput
              autoComplete="postal-address-locality"
              className="my-1.5"
              control={control}
              label="City"
              name="city"
              rules={{ required: "required" }}
              onSubmitEditing={focus("state")}
              textContentType="addressCity"
            />

            <View className="flex-row flex-wrap my-4">
              <View className="flex-1">
                <StateDropdown
                  control={control}
                  name="state"
                  rules={{ required: "required" }}
                  onDismiss={focus("postalCode")}
                />
              </View>

              <View className="flex-1">
                <TextInput
                  autoComplete="postal-address-extended-postal-code"
                  className="ml-2.5 grow-0"
                  control={control}
                  keyboardType="number-pad"
                  label="Zip Code"
                  maxLength={5}
                  name="postalCode"
                  onSubmitEditing={focus("ssn")}
                  rules={{ required: "required", pattern: /^\d{5}$/ }}
                  textContentType="postalCode"
                />
              </View>
            </View>
          </View>

          <Divider field="ssn" />

          <View className="px-7 mb-8">
            <Text className="mt-7 text-ink-darkest text-large-normal font-700">Social Security Number</Text>

            <Text className="mt-2 mb-7 text-small-normal font-400 text-ink-dark">
              Your SSN is required by the plan administrator to open a financial account.
            </Text>
            <SSNInput
              control={control}
              name="ssn"
              label="Social Security Number"
              blurOnSubmit={true}
              testID={"ssn"}
              rules={{ required: "required", pattern: /^\d{3}\-\d{2}\-\d{4}$/ }}
            />
          </View>

          <Divider />

          <Controller
            name="attested"
            control={control}
            rules={{ required: "Attestation is required" }}
            render={({ field: { ref, ...fieldProps }, fieldState: { error } }) => (
              <CheckboxItem
                {...fieldProps}
                label="I attest the above information is accurate."
                labelStyle="text-regular-normal font-400 text-left ml-1"
                className="items-center my-4"
                error={error}
              />
            )}
          />

          <ActionsContainer errorAlert errorMessage={errorMessage} onCloseErrorMessage={setErrorMessage}>
            <Button
              loading={loading}
              disabled={isSubmitting}
              isValid={isValid}
              mode="contained"
              onPress={handleSubmit(onSubmit, onSubmitError)}
              testID="submit"
            >
              Submit
            </Button>
          </ActionsContainer>
        </ScrollableContainer>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
